import { LanguageProvider } from "./i18n/Language";
import React from "react";
import ReactDOM from "react-dom/client";
import {
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";

import "@/index.css";
import App from "@/App";

/**
 * React Query configuration.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * Register the MK Transfè service worker.
 *
 * Registration is done after the page loads so it does not
 * slow down the initial React rendering.
 *
 * The actual push subscription can still be created later
 * after the authenticated user logs in.
 */
async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    console.warn(
      "Service workers are not supported by this browser.",
    );
    return null;
  }

  try {
    const registration =
      await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      });

    console.info(
      "MK Transfè service worker registered:",
      registration.scope,
    );

    /**
     * Check periodically for a new service worker version.
     */
    registration.update().catch((error) => {
      console.warn(
        "Unable to check for service worker update:",
        error,
      );
    });

    /**
     * Detect when a new service worker is installed.
     */
    registration.addEventListener("updatefound", () => {
      const installingWorker = registration.installing;

      if (!installingWorker) {
        return;
      }

      installingWorker.addEventListener(
        "statechange",
        () => {
          if (
            installingWorker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            console.info(
              "A new MK Transfè version is available.",
            );
          }
        },
      );
    });

    return registration;
  } catch (error) {
    console.error(
      "MK Transfè service worker registration failed:",
      error,
    );

    return null;
  }
}

/**
 * Register the service worker once the page has fully loaded.
 */
if ("serviceWorker" in navigator) {
  window.addEventListener(
    "load",
    () => {
      registerServiceWorker();
    },
    { once: true },
  );
}

/**
 * Find the React root element.
 */
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error(
    'React root element not found. Verify that index.html contains <div id="root"></div>.',
  );
}

/**
 * Start the React application.
 */
const root = ReactDOM.createRoot(rootElement);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider><App /></LanguageProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);